/**
 * Shared right-side header utilities: theme, notifications, profile menu, military flag.
 * Used by site Header and Me/Teach PWA shells so chrome stays consistent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Article as BlogIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Favorite,
  Login,
  Logout,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Person,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { K9_OFFLINE_AUTH_V2_ENABLED } from '../../config/featureFlags';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useSubscription } from '../../hooks/useSubscription';
import { LearningLabNavControl } from '../learning-lab/LearningLabNavControl';
import ThemeToggle from '../common/ThemeToggle';
import ImpersonationStatus from '../common/ImpersonationStatus';
import SessionCountdown from './SessionCountdown';
import UnifiedNotificationDropdown from '../notifications/UnifiedNotificationDropdown';
import { SignupIntentModal } from './SignupIntentModal';
import { PEOPLE_PROFILE_RETURN_KEY } from '../people/PeopleProfileCta';
import { SanitizedHrefButton } from '../common/SanitizedHrefAnchors';
import VisuallyHiddenText from '../common/VisuallyHiddenText';
import USAFlagIcon from '../icons/USAFlagIcon';
import { getUserAvatarDisplayUrl, handleImageError } from '../../utils/imageUtils';
import { getAuthUrl, getNavigationUrl } from '../../utils/subdomainUtils';
import { resolveMePwaHomePath, resolveMePwaLogoutPath } from '../../../shared/pwa/mePwaNavigation';
import { resolveTeachPwaHomePath, resolveTeachPwaLogoutPath } from '../../../shared/pwa/teachPwaNavigation';
import { toHttpHrefAttribute } from '../../../shared/utils/sanitizeHttpUrlForHref';
import {
  shouldOpenSignupIntentFromQuery,
  stripSignupQueryFromSearch,
} from './signupIntentQuery';
import { isSessionAuthenticatedAsync } from '../../utils/storageUtils';
import { safeAPICall } from '../../utils/trackingUtils';
import { logger } from '../../utils/logger';
import { NOTIFICATIONS_CHANGED_EVENT } from '../../utils/notificationEvents';
import { PROFILE_AND_SETTINGS_NAV_PATH } from '../profile/profileSettingsTabs';
import { TOUR_DOM_EVENTS } from '../onboarding/onboardingDomEvents';
import userService from '../../services/userService';
import { headerUtilityIconButtonSx } from '../../constants/headerChromeStyles';
import { useMaterialUIDropdownClose } from '../../hooks/useMaterialUIDropdownClose';

interface BadgeIconProps {
  children: React.ReactNode;
  badgeContent?: number;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

const BadgeIcon: React.FC<BadgeIconProps> = ({
  children,
  badgeContent,
  color = 'error',
}) => (
  <Badge
    badgeContent={badgeContent && badgeContent > 0 ? badgeContent : undefined}
    color={color}
    overlap="circular"
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    sx={{
      '& .MuiBadge-badge': {
        backgroundColor: color === 'error' ? '#d32f2f' : undefined,
        color: 'white',
        fontSize: '11px',
        fontWeight: 600,
        minWidth: '18px',
        height: '18px',
        borderRadius: '9px',
        border: '2px solid white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      },
    }}
  >
    {children}
  </Badge>
);

const MILITARY_NONE_ID = 6;

export type HeaderAccountUtilitiesVariant = 'default' | 'me-pwa' | 'teach-pwa';

interface HeaderAccountUtilitiesProps {
  variant?: HeaderAccountUtilitiesVariant;
}

const HeaderAccountUtilities: React.FC<HeaderAccountUtilitiesProps> = ({
  variant = 'default',
}) => {
  const theme = useTheme();
  const isMePwaVariant = variant === 'me-pwa';
  const isTeachPwaVariant = variant === 'teach-pwa';
  const isFieldPwaVariant = isMePwaVariant || isTeachPwaVariant;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { isAuthenticated, user, logout, isLoading, isInitializing, authMode } = useAuth();
  const { hasPremium } = useSubscription();
  const { state: onboardingState } = useOnboarding();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [signupIntentModalOpen, setSignupIntentModalOpen] = useState(false);
  const [signupIntentRedirectUrl, setSignupIntentRedirectUrl] = useState<string | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [militaryModalOpen, setMilitaryModalOpen] = useState(false);
  const [militaryBranchName, setMilitaryBranchName] = useState<string | null>(null);
  const [loadingBranchName, setLoadingBranchName] = useState(false);

  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedCountsRef = useRef(false);
  const fetchCountsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loginUrl = useMemo(() => {
    const normalized = location.pathname.replace(/\/+$/, '') || '/';
    let raw: string;
    if (isMePwaVariant) {
      raw = getAuthUrl('/login', {
        redirectUrl: resolveMePwaHomePath(
          location.pathname,
          typeof window !== 'undefined' ? window.location.hostname : undefined,
        ),
        includeRedirect: true,
      });
    } else if (isTeachPwaVariant) {
      raw = getAuthUrl('/login', {
        redirectUrl: resolveTeachPwaHomePath(
          location.pathname,
          typeof window !== 'undefined' ? window.location.hostname : undefined,
        ),
        includeRedirect: true,
      });
    } else if (normalized === '/secondary-employment' && typeof window !== 'undefined') {
      raw = getAuthUrl('/login', {
        redirectUrl: '/secondary-employment/dashboard?tab=employer',
        includeRedirect: true,
      });
    } else {
      raw = getAuthUrl('/login');
    }
    return toHttpHrefAttribute(raw) || '/login';
  }, [isMePwaVariant, isTeachPwaVariant, location.pathname]);

  useEffect(() => {
    if (isFieldPwaVariant) {
      return;
    }
    if (!location.search.includes('signup=')) {
      return;
    }
    if (isInitializing) {
      return;
    }
    if (shouldOpenSignupIntentFromQuery(location.search, isAuthenticated, isInitializing)) {
      try {
        const stored = sessionStorage.getItem(PEOPLE_PROFILE_RETURN_KEY);
        if (stored) setSignupIntentRedirectUrl(stored);
      } catch {
        /* ignore */
      }
      const params = new URLSearchParams(location.search);
      if (params.get('people_profile') === 'finish' && !signupIntentRedirectUrl) {
        const finish = `${location.pathname}?people_profile=finish`;
        setSignupIntentRedirectUrl(finish);
      }
      setSignupIntentModalOpen(true);
    }
    const nextSearch = stripSignupQueryFromSearch(location.search);
    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  }, [isFieldPwaVariant, location.pathname, location.search, isAuthenticated, isInitializing, navigate]);

  useEffect(() => {
    const onOpen = (ev: Event) => {
      const detail = (ev as CustomEvent<{ redirectUrl?: string }>).detail;
      if (detail?.redirectUrl) {
        setSignupIntentRedirectUrl(detail.redirectUrl);
      }
      setSignupIntentModalOpen(true);
    };
    window.addEventListener('apt:open-signup-intent', onOpen as EventListener);
    return () => window.removeEventListener('apt:open-signup-intent', onOpen as EventListener);
  }, []);

  const isOfflineAuthMode = K9_OFFLINE_AUTH_V2_ENABLED && authMode === 'offline';

  const hasMilitaryStatus = useMemo(() => {
    if (!user) return false;
    const branchId = user.military_branch_id ?? (user as { militaryBranchId?: number }).militaryBranchId;
    const serviceId = user.military_service_id ?? (user as { militaryServiceId?: number }).militaryServiceId;
    const hasRealBranch = !!(branchId && branchId > 0);
    const hasRealService = !!(serviceId && serviceId > 0 && serviceId !== MILITARY_NONE_ID);
    return hasRealBranch || hasRealService;
  }, [user]);

  const fetchUnreadMessageCount = async () => {
    if (!isAuthenticated || !user?.id) {
      setUnreadMessageCount(0);
      return;
    }
    try {
      const isAuth = await isSessionAuthenticatedAsync();
      if (!isAuth) {
        setUnreadMessageCount(0);
        return;
      }
      const result = await safeAPICall(async () => {
        const response = await fetch('/api/messaging/messages/unread-count', {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) {
          const data = await response.json();
          return data.count || 0;
        }
        return 0;
      }, 0, 'Unread Messages');
      setUnreadMessageCount(result);
    } catch {
      setUnreadMessageCount(0);
    }
  };

  const fetchUnreadNotificationCount = async () => {
    if (!isAuthenticated || !user?.id) {
      setUnreadNotificationCount(0);
      return;
    }
    try {
      const isAuth = await isSessionAuthenticatedAsync();
      if (!isAuth) {
        setUnreadNotificationCount(0);
        return;
      }
      const result = await safeAPICall(async () => {
        const response = await fetch('/api/notifications/unified?limit=1', {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) {
          const data = await response.json();
          return data.data?.counts?.unreadTotal || 0;
        }
        return 0;
      }, 0, 'Unread Notifications & Messages');
      setUnreadNotificationCount(result);
    } catch {
      setUnreadNotificationCount(0);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.id && !isLoading && !isInitializing && !hasFetchedCountsRef.current) {
      if (fetchCountsTimeoutRef.current) {
        clearTimeout(fetchCountsTimeoutRef.current);
      }
      fetchCountsTimeoutRef.current = setTimeout(async () => {
        hasFetchedCountsRef.current = true;
        const isAuth = await isSessionAuthenticatedAsync();
        if (isAuth) {
          fetchUnreadMessageCount();
          setTimeout(() => {
            fetchUnreadNotificationCount();
          }, 300);
        }
      }, 1500);
      messageIntervalRef.current = setInterval(fetchUnreadMessageCount, 300000);
      notificationIntervalRef.current = setInterval(fetchUnreadNotificationCount, 300000);
      return () => {
        if (fetchCountsTimeoutRef.current) {
          clearTimeout(fetchCountsTimeoutRef.current);
          fetchCountsTimeoutRef.current = null;
        }
        if (messageIntervalRef.current) {
          clearInterval(messageIntervalRef.current);
          messageIntervalRef.current = null;
        }
        if (notificationIntervalRef.current) {
          clearInterval(notificationIntervalRef.current);
          notificationIntervalRef.current = null;
        }
      };
    }
    setUnreadMessageCount(0);
    setUnreadNotificationCount(0);
    hasFetchedCountsRef.current = false;
    return undefined;
  }, [isAuthenticated, user?.id, isLoading, isInitializing]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }
    const onChanged = () => {
      void fetchUnreadNotificationCount();
    };
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    const handleOpenNavDropdown = (event: Event) => {
      const menu = (event as CustomEvent<{ menu?: string }>).detail?.menu;
      if (menu === 'user' && userMenuButtonRef.current) {
        setAnchorEl((current) => {
          const btn = userMenuButtonRef.current;
          if (!btn) {
            return current;
          }
          return current === btn ? current : btn;
        });
      }
    };
    const handleCloseNavDropdowns = () => {
      setAnchorEl(null);
      setNotificationAnchorEl(null);
    };
    window.addEventListener(TOUR_DOM_EVENTS.OPEN_NAV_DROPDOWN, handleOpenNavDropdown);
    window.addEventListener(TOUR_DOM_EVENTS.CLOSE_NAV_DROPDOWNS, handleCloseNavDropdowns);
    return () => {
      window.removeEventListener(TOUR_DOM_EVENTS.OPEN_NAV_DROPDOWN, handleOpenNavDropdown);
      window.removeEventListener(TOUR_DOM_EVENTS.CLOSE_NAV_DROPDOWNS, handleCloseNavDropdowns);
    };
  }, []);

  const handleProfileMenuClose = useCallback((_event?: React.SyntheticEvent | {}, _reason?: string) => {
    setAnchorEl(null);
  }, []);

  const handleProfileMenuToggle = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget;
    setNotificationAnchorEl(null);
    setAnchorEl((current) => (current ? null : target));
  }, []);

  const closeUserMenuFromOutside = useCallback((open: boolean) => {
    if (!open) {
      setAnchorEl(null);
    }
  }, []);

  const closeNotificationMenuFromOutside = useCallback((open: boolean) => {
    if (!open) {
      setNotificationAnchorEl(null);
    }
  }, []);

  // MUI Menu click-away is often blocked by analytics capture listeners; close explicitly.
  const userMenuDropdownCloseStates = useMemo(
    () => [
      {
        isOpen: Boolean(anchorEl) && !onboardingState.isRunning,
        setOpen: closeUserMenuFromOutside,
      },
      {
        isOpen: Boolean(notificationAnchorEl) && !onboardingState.isRunning,
        setOpen: closeNotificationMenuFromOutside,
      },
    ],
    [
      anchorEl,
      notificationAnchorEl,
      onboardingState.isRunning,
      closeUserMenuFromOutside,
      closeNotificationMenuFromOutside,
    ],
  );
  useMaterialUIDropdownClose(userMenuDropdownCloseStates);

  // With disableScrollLock, close header menus on page scroll so they don't detach from sticky chrome.
  useEffect(() => {
    if ((!anchorEl && !notificationAnchorEl) || onboardingState.isRunning) {
      return;
    }
    const handleScrollClose = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('.MuiMenu-root, .MuiPopover-root, [role="menu"]')
      ) {
        return;
      }
      setAnchorEl(null);
      setNotificationAnchorEl(null);
    };
    window.addEventListener('scroll', handleScrollClose, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handleScrollClose, true);
    };
  }, [anchorEl, notificationAnchorEl, onboardingState.isRunning]);

  const profileImageSrc = useMemo(() => {
    const raw =
      user?.profile_image ||
      (user as { profileImage?: string | null } | null | undefined)?.profileImage ||
      null;
    if (!raw || !String(raw).trim()) {
      return '';
    }
    // Prefer direct CDN for <img> display; getUserAvatar still proxies for CORS/crop.
    return getUserAvatarDisplayUrl(String(raw).trim(), user?.id || 0);
  }, [user]);

  const handleLogout = useCallback(() => {
    handleProfileMenuClose();
    logout().catch((error) => {
      logger.error('HeaderAccountUtilities: Logout cleanup failed', error, {});
    });
    if (isMePwaVariant) {
      navigate(resolveMePwaLogoutPath(location.pathname, typeof window !== 'undefined' ? window.location.hostname : undefined), {
        replace: true,
        state: {
          logoutCompleted: true,
          mePwaReturnPath: resolveMePwaHomePath(
            location.pathname,
            typeof window !== 'undefined' ? window.location.hostname : undefined,
          ),
        },
      });
      return;
    }
    if (isTeachPwaVariant) {
      navigate(resolveTeachPwaLogoutPath(location.pathname, typeof window !== 'undefined' ? window.location.hostname : undefined), {
        replace: true,
        state: {
          logoutCompleted: true,
          teachPwaReturnPath: resolveTeachPwaHomePath(
            location.pathname,
            typeof window !== 'undefined' ? window.location.hostname : undefined,
          ),
        },
      });
      return;
    }
    navigate('/logout-confirmation', {
      replace: true,
      state: { logoutCompleted: true },
    });
  }, [handleProfileMenuClose, isMePwaVariant, isTeachPwaVariant, location.pathname, logout, navigate]);

  const handleFlagClick = useCallback(async () => {
    if (!user?.military_branch_id) return;
    setMilitaryModalOpen(true);
    setLoadingBranchName(true);
    try {
      const response = await userService.getMilitaryBranches();
      if (response.success && response.data) {
        const branch = response.data.find((b: { id: number }) => b.id === user.military_branch_id);
        setMilitaryBranchName(branch?.name ?? null);
      } else {
        setMilitaryBranchName(null);
      }
    } catch (error) {
      logger.error('Error fetching military branch:', error, {});
      setMilitaryBranchName(null);
    } finally {
      setLoadingBranchName(false);
    }
  }, [user]);

  const userMenuItems = useMemo(() => [
    { text: 'Dashboard', path: getNavigationUrl('/dashboard'), icon: <DashboardIcon /> },
    {
      text: 'Notifications',
      path: getNavigationUrl('/notifications'),
      icon: <NotificationsIcon />,
      badgeContent: unreadNotificationCount,
    },
    {
      text: 'Messages',
      path: getNavigationUrl('/messages'),
      icon: <MessageIcon />,
      badgeContent: unreadMessageCount,
    },
    { text: 'My Blogs', path: getNavigationUrl('/blog/manage'), icon: <BlogIcon /> },
    { text: 'My Favorites', path: getNavigationUrl('/favorites'), icon: <Favorite /> },
    { text: 'Profile & Settings', path: getNavigationUrl(PROFILE_AND_SETTINGS_NAV_PATH), icon: <Person /> },
  ], [unreadNotificationCount, unreadMessageCount]);

  const focusTourTooltip = () => {
    const tooltip = document.querySelector('[data-tour-tooltip="true"]') as HTMLElement | null;
    tooltip?.focus();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          flexShrink: 0,
          minWidth: { xs: 'auto', sm: '200px' },
          justifyContent: 'flex-end',
          ml: 'auto',
        }}
      >
        <ImpersonationStatus variant="compact" />
        <Box data-tour="nav-theme-toggle">
          <ThemeToggle
            size="small"
            sx={{
              p: { xs: 1, sm: 1 },
              minWidth: 'auto',
              minHeight: 'auto',
              '& .MuiSvgIcon-root': { fontSize: { xs: 20, md: 18 } },
            }}
          />
        </Box>

        {isAuthenticated && !isFieldPwaVariant ? <LearningLabNavControl /> : null}

        {isAuthenticated && !isFieldPwaVariant ? (
          <>
            <SessionCountdown />
            <IconButton
              id="notification-button"
              data-tour="notifications"
              data-tour-stable="notifications"
              onClick={(e) => {
                setAnchorEl(null);
                const target = e.currentTarget;
                setNotificationAnchorEl((current) => (current ? null : target));
              }}
              color="inherit"
              sx={{ mr: { xs: 0.5, sm: 0.75 }, ...headerUtilityIconButtonSx }}
              aria-label={`Notifications and messages${unreadNotificationCount > 0 ? ` (${unreadNotificationCount} unread)` : ''}`}
              aria-haspopup="true"
              aria-expanded={Boolean(notificationAnchorEl)}
            >
              <BadgeIcon badgeContent={unreadNotificationCount}>
                <NotificationsIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              </BadgeIcon>
            </IconButton>

            <IconButton
              ref={userMenuButtonRef}
              id="user-menu-button"
              data-tour="nav-user-menu"
              data-tour-stable="nav-user-menu"
              onClick={handleProfileMenuToggle}
              sx={{ p: { xs: 0.25, sm: 0.375 }, ...headerUtilityIconButtonSx }}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl)}
              aria-label="User menu"
            >
              <Avatar
                key={profileImageSrc || 'no-image'}
                sx={{
                  width: { xs: 28, sm: 34 },
                  height: { xs: 28, sm: 34 },
                  border: hasPremium ? '3px solid #FFD700' : '2px solid rgba(255, 255, 255, 0.8)',
                  boxShadow: hasPremium
                    ? '0 2px 12px rgba(255, 215, 0, 0.4), 0 0 0 1px rgba(255, 215, 0, 0.2)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                }}
                src={profileImageSrc || undefined}
                imgProps={{
                  onError: (e) =>
                    handleImageError(
                      e as React.SyntheticEvent<HTMLImageElement, Event>,
                      user?.id || 0,
                    ),
                }}
              >
                {user?.first_name?.[0]}{user?.last_name?.[0] || 'U'}
              </Avatar>
            </IconButton>

            {hasMilitaryStatus ? (
              <Tooltip title="Thank you for your service" arrow>
                <Box
                  component="span"
                  onClick={handleFlagClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    ml: { xs: 0.5, sm: 1 },
                    lineHeight: 0,
                    '&:hover': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s ease-in-out',
                    },
                  }}
                  aria-label="Military service recognition"
                >
                  <USAFlagIcon
                    size="medium"
                    sx={{
                      width: 56,
                      height: 30,
                      objectFit: 'contain',
                      filter: theme.palette.mode === 'dark' ? 'grayscale(100%)' : 'none',
                      transition: 'filter 0.2s ease-in-out',
                    }}
                  />
                </Box>
              </Tooltip>
            ) : null}

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={(event, reason) => {
                if (onboardingState.isRunning) {
                  if (reason === 'escapeKeyDown' || reason === 'tabKeyDown') {
                    handleProfileMenuClose(event, reason);
                    requestAnimationFrame(focusTourTooltip);
                  }
                  return;
                }
                handleProfileMenuClose(event, reason);
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              // Keep sticky AppBar pinned while the account menu is open.
              disableScrollLock
              disableAutoFocusItem={!onboardingState.isRunning}
              MenuListProps={{ 'aria-labelledby': 'user-menu-button' }}
            >
              {userMenuItems.map((item) => {
                const userMenuTourId = item.text.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and').replace(/[^a-z0-9-]/g, '');
                return (
                  <MenuItem
                    key={item.text}
                    data-tour={`nav-user-${userMenuTourId}`}
                    component="a"
                    href={item.path}
                    onClick={(e) => {
                      if (!onboardingState.isRunning) {
                        handleProfileMenuClose();
                      } else {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    sx={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <ListItemIcon>
                      {item.badgeContent !== undefined ? (
                        <BadgeIcon badgeContent={item.badgeContent} color={item.text === 'Messages' ? 'primary' : 'error'}>
                          {item.icon}
                        </BadgeIcon>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {item.text}
                  </MenuItem>
                );
              })}
              <Tooltip
                title={isOfflineAuthMode ? 'Reconnect to sign out' : ''}
                disableHoverListener={!isOfflineAuthMode}
                disableFocusListener={!isOfflineAuthMode}
              >
                <span>
                  <MenuItem
                    data-tour="nav-user-logout"
                    onClick={isOfflineAuthMode ? undefined : handleLogout}
                    disabled={isOfflineAuthMode}
                    sx={{
                      opacity: isOfflineAuthMode ? 0.55 : 1,
                      cursor: isOfflineAuthMode ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ListItemIcon><Logout /></ListItemIcon>
                    Logout
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>
          </>
        ) : isAuthenticated ? (
          <>
            <IconButton
              ref={userMenuButtonRef}
              id="user-menu-button"
              data-tour="nav-user-menu"
              data-tour-stable="nav-user-menu"
              onClick={handleProfileMenuToggle}
              sx={{ p: { xs: 0.25, sm: 0.375 }, ...headerUtilityIconButtonSx }}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl)}
              aria-label="User menu"
            >
              <Avatar
                key={profileImageSrc || 'no-image'}
                sx={{
                  width: { xs: 28, sm: 34 },
                  height: { xs: 28, sm: 34 },
                  border: hasPremium ? '3px solid #FFD700' : '2px solid rgba(255, 255, 255, 0.8)',
                  boxShadow: hasPremium
                    ? '0 2px 12px rgba(255, 215, 0, 0.4), 0 0 0 1px rgba(255, 215, 0, 0.2)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                }}
                src={profileImageSrc || undefined}
                imgProps={{
                  onError: (e) =>
                    handleImageError(
                      e as React.SyntheticEvent<HTMLImageElement, Event>,
                      user?.id || 0,
                    ),
                }}
              >
                {user?.first_name?.[0]}{user?.last_name?.[0] || 'U'}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={(event, reason) => {
                if (onboardingState.isRunning) {
                  if (reason === 'escapeKeyDown' || reason === 'tabKeyDown') {
                    handleProfileMenuClose(event, reason);
                    requestAnimationFrame(focusTourTooltip);
                  }
                  return;
                }
                handleProfileMenuClose(event, reason);
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              // Keep sticky AppBar pinned while the account menu is open.
              disableScrollLock
              disableAutoFocusItem={!onboardingState.isRunning}
              MenuListProps={{ 'aria-labelledby': 'user-menu-button' }}
            >
              <Tooltip
                title={isOfflineAuthMode ? 'Reconnect to sign out' : ''}
                disableHoverListener={!isOfflineAuthMode}
                disableFocusListener={!isOfflineAuthMode}
              >
                <span>
                  <MenuItem
                    data-tour="nav-user-logout"
                    onClick={isOfflineAuthMode ? undefined : handleLogout}
                    disabled={isOfflineAuthMode}
                    sx={{
                      opacity: isOfflineAuthMode ? 0.55 : 1,
                      cursor: isOfflineAuthMode ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ListItemIcon><Logout /></ListItemIcon>
                    Logout
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center' }}>
            <SanitizedHrefButton
              sanitizedHref={loginUrl}
              color="inherit"
              aria-label="Login"
              title="Login"
              startIcon={isMobile ? undefined : <Login aria-hidden />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.primary',
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                borderRadius: 2,
                ...headerUtilityIconButtonSx,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 'auto' },
              }}
            >
              {isMobile ? (
                <>
                  <Login sx={{ fontSize: 20 }} aria-hidden />
                  <VisuallyHiddenText>Login</VisuallyHiddenText>
                </>
              ) : (
                'Login'
              )}
            </SanitizedHrefButton>
            {!isFieldPwaVariant ? (
            <Button
              type="button"
              variant="contained"
              onClick={() => setSignupIntentModalOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 'auto' },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Signup
            </Button>
            ) : null}
          </Box>
        )}
      </Box>

      <SignupIntentModal
        open={!isFieldPwaVariant && signupIntentModalOpen}
        redirectUrl={signupIntentRedirectUrl}
        onClose={() => {
          setSignupIntentModalOpen(false);
          setSignupIntentRedirectUrl(null);
        }}
      />

      <Popover
        id="notification-menu"
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Keep sticky AppBar pinned while notifications are open.
        disableScrollLock
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <ClickAwayListener onClickAway={() => setNotificationAnchorEl(null)}>
          <Box component="div" sx={{ outline: 'none' }}>
            <UnifiedNotificationDropdown onClose={() => setNotificationAnchorEl(null)} />
          </Box>
        </ClickAwayListener>
      </Popover>

      <Dialog
        open={militaryModalOpen}
        onClose={() => setMilitaryModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 0, p: 0 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Thank You for Your Service
          </Typography>
          <IconButton onClick={() => setMilitaryModalOpen(false)} size="small" aria-label="Close military service dialog" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <USAFlagIcon
                size="large"
                sx={{
                  width: 180,
                  height: 95,
                  objectFit: 'contain',
                  filter: theme.palette.mode === 'dark' ? 'grayscale(100%)' : 'none',
                }}
              />
            </Box>
            {loadingBranchName ? (
              <Typography variant="body1" color="text.secondary">Loading...</Typography>
            ) : militaryBranchName ? (
              <>
                <Typography variant="h6" component="div" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  {militaryBranchName}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                  Thank you for your service and continued dedication in law enforcement.
                </Typography>
              </>
            ) : (
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                Thank you for your service and continued dedication in law enforcement.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button onClick={() => setMilitaryModalOpen(false)} variant="contained" color="primary" fullWidth sx={{ py: 1.5 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HeaderAccountUtilities;
